import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Pencil, Plus, Power, RefreshCw, RotateCcw, Shield, X } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";

// =====================================================================
// Settings → Branches
//
// Owner-only branch management. The RPCs enforce the same rule in the
// database, so this page is just the operator surface for that contract.
// =====================================================================

export default function SettingsBranches() {
  const { clinicRole } = useAuth();
  const canManageBranches = clinicRole === "clinic_admin";
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchForm, setBranchForm] = useState({ name: "", code: "" });
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", code: "" });
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [updatingBranchId, setUpdatingBranchId] = useState(null);
  const [pendingAddOnReview, setPendingAddOnReview] = useState(null);

  const loadBranches = useCallback(async () => {
    if (!canManageBranches) return [];
    setBranchesLoading(true);
    const { data, error: rpcError } = await supabase.rpc("list_clinic_branches_for_management", {
      p_include_inactive: true,
    });
    setBranchesLoading(false);
    if (rpcError) {
      toast.error(rpcError.message || "Could not load branches.");
      return null;
    }
    const rows = Array.isArray(data) ? data : [];
    setBranches(rows);
    return rows;
  }, [canManageBranches]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  async function submitCreateBranch(name, code) {
    setCreatingBranch(true);
    const payload = { name };
    if (code) payload.code = code;
    const { data, error: rpcError } = await supabase.rpc("create_clinic_branch", {
      p_input: payload,
    });
    setCreatingBranch(false);
    if (rpcError) {
      toast.error(rpcError.message || "Could not create branch.");
      return;
    }
    setBranchForm({ name: "", code: "" });
    toast.success(branchCreateSuccessMessage(data));
    await loadBranches();
  }

  async function handleCreateBranch() {
    const name = branchForm.name.trim();
    const code = branchForm.code.trim();
    if (!name || creatingBranch) return;

    const branchRows = branches.length > 0 ? branches : await loadBranches();
    if (!branchRows) return;

    const summary = branchRows[0] || {};
    const activeCount = Number(summary.active_branch_count ?? branchRows.filter((branch) => branch.status === "active").length);
    const includedCount = Number(summary.included_branch_count ?? 2);
    if (activeCount >= includedCount) {
      setPendingAddOnReview({ kind: "create", name, code });
      return;
    }

    await submitCreateBranch(name, code);
  }

  function startEditingBranch(branch) {
    setEditingBranchId(branch.id);
    setEditForm({ name: branch.name || "", code: branch.code || "" });
  }

  function cancelEditingBranch() {
    setEditingBranchId(null);
    setEditForm({ name: "", code: "" });
  }

  async function handleUpdateBranch(branchId, payload, successMessage) {
    if (!branchId || updatingBranchId) return false;
    setUpdatingBranchId(branchId);
    const { error: rpcError } = await supabase.rpc("update_clinic_branch", {
      p_branch_id: branchId,
      p_input: payload,
    });
    setUpdatingBranchId(null);
    if (rpcError) {
      toast.error(rpcError.message || "Could not update branch.");
      return false;
    }
    toast.success(successMessage);
    await loadBranches();
    return true;
  }

  async function handleSaveBranch(branch) {
    const name = editForm.name.trim();
    const code = editForm.code.trim();
    if (!name) return;
    const payload = { name };
    if (code) payload.code = code;
    const ok = await handleUpdateBranch(branch.id, payload, "Branch updated.");
    if (ok) cancelEditingBranch();
  }

  async function handleToggleBranchStatus(branch) {
    const nextStatus = branch.status === "active" ? "inactive" : "active";
    if (nextStatus === "inactive") {
      const ok = window.confirm(`Deactivate ${branch.name}? This removes it from next invoice add-on calculations. Existing records stay linked.`);
      if (!ok) return;
    } else {
      const activeCount = Number(branch.active_branch_count ?? branches.filter((row) => row.status === "active").length);
      const includedCount = Number(branch.included_branch_count ?? 2);
      if (activeCount >= includedCount) {
        setPendingAddOnReview({ kind: "reactivate", branch });
        return;
      }
    }
    await handleUpdateBranch(
      branch.id,
      { status: nextStatus },
      nextStatus === "active" ? "Branch reactivated." : "Branch deactivated."
    );
  }

  async function confirmPendingAddOnReview() {
    const review = pendingAddOnReview;
    if (!review) return;
    setPendingAddOnReview(null);
    if (review.kind === "create") {
      await submitCreateBranch(review.name, review.code);
      return;
    }
    if (review.kind === "reactivate" && review.branch?.id) {
      await handleUpdateBranch(review.branch.id, { status: "active" }, "Branch reactivated.");
    }
  }

  if (!canManageBranches) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="settings-branches-denied">
        <Shield className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">Only clinic owners can manage branches.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-branches" data-tutorial-id="settings-branches">
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <h1 className="text-lg font-semibold text-slate-900">Branches</h1>
        <p className="text-xs text-slate-500">
          Active branches beyond the included limit appear as invoice add-ons.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4" data-testid="settings-branches-card" data-tutorial-id="settings-branches-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Clinic branches</h2>
            <p className="mt-1 text-xs text-slate-500">
              Branches are deactivated when closed, not deleted.
            </p>
          </div>
          <button
            type="button"
            onClick={loadBranches}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            data-testid="reload-branches-button"
          >
            {branchesLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200">
          <div className="grid grid-cols-[minmax(0,1fr)_120px_130px_150px] bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            <span>Branch</span>
            <span>Status</span>
            <span>Add-on</span>
            <span>Actions</span>
          </div>
          {branches.length === 0 ? (
            <div className="px-3 py-4 text-sm text-slate-500">No branches found.</div>
          ) : branches.map((branch) => (
            <div key={branch.id} className="grid grid-cols-[minmax(0,1fr)_120px_130px_150px] items-center border-t border-slate-100 px-3 py-2 text-sm">
              <div className="min-w-0">
                {editingBranchId === branch.id ? (
                  <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_130px]">
                    <input
                      data-testid={`edit-branch-name-${branch.id}`}
                      value={editForm.name}
                      onChange={(event) => setEditForm((form) => ({ ...form, name: event.target.value }))}
                      className="h-9 rounded-lg border border-slate-300 px-2 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                      maxLength={120}
                    />
                    <input
                      data-testid={`edit-branch-code-${branch.id}`}
                      value={editForm.code}
                      onChange={(event) => setEditForm((form) => ({ ...form, code: event.target.value }))}
                      className="h-9 rounded-lg border border-slate-300 px-2 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                      maxLength={40}
                    />
                  </div>
                ) : (
                  <>
                    <p className="truncate font-semibold text-slate-900">{branch.name}</p>
                    <p className="truncate text-xs text-slate-500">{branch.code || "No code"}</p>
                  </>
                )}
              </div>
              <span className="capitalize text-slate-600">{branch.status}</span>
              <span className={branch.billable_add_on ? "font-semibold text-amber-700" : "text-slate-500"}>
                {branch.billable_add_on ? "₱1,500/mo" : "Included"}
              </span>
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                {editingBranchId === branch.id ? (
                  <>
                    <button
                      type="button"
                      title="Save branch"
                      data-testid={`save-branch-${branch.id}`}
                      disabled={!editForm.name.trim() || updatingBranchId === branch.id}
                      onClick={() => handleSaveBranch(branch)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-teal-200 text-teal-700 hover:bg-teal-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300"
                    >
                      {updatingBranchId === branch.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      title="Cancel edit"
                      data-testid={`cancel-edit-branch-${branch.id}`}
                      onClick={cancelEditingBranch}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      title="Edit branch"
                      data-testid={`edit-branch-${branch.id}`}
                      onClick={() => startEditingBranch(branch)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title={branch.status === "active" ? "Deactivate branch" : "Reactivate branch"}
                      data-testid={`${branch.status === "active" ? "deactivate" : "reactivate"}-branch-${branch.id}`}
                      disabled={updatingBranchId === branch.id}
                      onClick={() => handleToggleBranchStatus(branch)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                    >
                      {updatingBranchId === branch.id ? <Loader2 className="h-4 w-4 animate-spin" /> : branch.status === "active" ? <Power className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_160px_auto]">
          <input
            data-testid="branch-name-input"
            value={branchForm.name}
            onChange={(event) => setBranchForm((form) => ({ ...form, name: event.target.value }))}
            placeholder="Branch name"
            maxLength={120}
            className="h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
          />
          <input
            data-testid="branch-code-input"
            value={branchForm.code}
            onChange={(event) => setBranchForm((form) => ({ ...form, code: event.target.value }))}
            placeholder="Code"
            maxLength={40}
            className="h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
          />
          <button
            type="button"
            data-testid="add-branch-button"
            data-tutorial-id="add-branch-button"
            onClick={handleCreateBranch}
            disabled={!branchForm.name.trim() || creatingBranch}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
          >
            {creatingBranch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </button>
        </div>
      </div>

      {pendingAddOnReview ? (
        <BranchAddOnReviewModal
          review={pendingAddOnReview}
          busy={creatingBranch || Boolean(updatingBranchId)}
          onCancel={() => setPendingAddOnReview(null)}
          onConfirm={confirmPendingAddOnReview}
        />
      ) : null}
    </div>
  );
}

function BranchAddOnReviewModal({ review, busy, onCancel, onConfirm }) {
  const isReactivate = review?.kind === "reactivate";
  const branchName = isReactivate ? review?.branch?.name : review?.name;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
      data-testid="branch-addon-review"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Review branch add-on</p>
            <h3 className="mt-1 text-lg font-bold text-slate-950">
              {isReactivate ? "Reactivate paid branch" : "Create paid branch"}
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {branchName ? `${branchName} is beyond the included branch limit.` : "This branch is beyond the included branch limit."}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="ml-auto rounded p-1 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close branch add-on review"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-semibold">This adds ₱1,500 per month.</p>
            <p className="mt-1 text-xs leading-5">
              Active paid billing may create an immediate prorated adjustment invoice. Future subscription invoices include the monthly branch add-on.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="confirm-branch-addon"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:bg-teal-500"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isReactivate ? "Reactivate branch" : "Create branch"}
          </button>
        </div>
      </div>
    </div>
  );
}

function branchCreateSuccessMessage(data) {
  const billingResult = data?.billing_result || {};
  if (billingResult.action === "created_branch_addon_adjustment_invoice") {
    return billingResult.invoice_number
      ? `Branch created. Adjustment invoice ${billingResult.invoice_number} created.`
      : "Branch created. Adjustment invoice created.";
  }
  return "Branch created.";
}
